import React, { useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';

import { useTheme } from 'contexts/ThemeContext';

import { ACTIVATION } from 'utils/rest';

import { Alert, Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  AsyncAutocomplete,
  Button,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';

const ErrorState = {
  errorName: false,
  errorKey: false,
  errorModel: false
};

const getGeoTargetOptions = (geoTargets = []) => {
  return geoTargets.map(
    ({ geo = false, audience = false, isDefault = false }) => {
      const hasGeoOption = geo;
      const hasAudienceOption = audience;
      const hasGeoPlusAudienceOption = hasGeoOption && hasAudienceOption;

      let label = 'Aucune';
      let value = '';

      if (hasGeoPlusAudienceOption) {
        label = 'Géo + audience';
        value = 'geoAndAudience';
      } else {
        if (geo) {
          label = 'Géographique';
          value = 'geo';
        }
        if (audience) {
          label = 'Audience';
          value = 'audience';
        }
      }

      return {
        geo,
        audience,
        label,
        isDefault,
        value
      };
    }
  );
};

export const ModalForm = props => {
  const {
    config,
    onValidate,
    onClose,
    actionName,
    connexionsGenerator,
    channels,
    langues,
    parentList,
    childrenList
  } = props;
  const { themeColors } = useTheme();
  const { designSystem } = themeColors;
  const generateDocumentation = documentation => {
    return Object.entries(documentation)
      .map(([key, value]) => {
        if (value) {
          return {
            langue: langues.find(el => el.key === key)?.label,
            url: value
          };
        }
        return null;
      })
      .filter(el => el !== null);
  };

  const geoTargetOptions = getGeoTargetOptions(config?.geoTargets);

  const initialState = {
    name: config?.name || '',
    key: config?.key || '',
    model: config?.model || '',
    types: config?.channels || [],
    minimumBudget: config?.minimumBudget || '',
    dailyMinimumBudget: config?.dailyMinimumBudget || '',
    minimumAudience: config?.minimumAudience || '',
    documentation:
      config?.documentation && langues?.length
        ? generateDocumentation(config?.documentation)
        : [],
    page: config?.page || false,
    enableMarginRate: config?.enableMarginRate || false,
    parentId: config?.parentId || null,
    children:
      (config?.id && childrenList.filter(el => el.parentId === config.id)) ||
      [],
    creativeTypes: config?.creativeTypes || [],
    friendlyName: config?.friendlyName || '',
    enableFormatSelection: config?.enableFormatSelection || false,
    availableFormats: config?.availableFormats || [],
    defaultGeoTarget: geoTargetOptions.find(el => el.isDefault)?.value
  };

  const creativeTypesOptions = [
    { label: 'Audio', value: 'audio' },
    { label: 'Image', value: 'image' },
    { label: 'Search', value: 'search' },
    { label: 'Video', value: 'video' },
    { label: 'SMS', value: 'sms' },
    { label: 'Email', value: 'email' }
  ];

  const [
    {
      name,
      model,
      key,
      types,
      minimumBudget,
      minimumAudience,
      dailyMinimumBudget,
      documentation,
      page,
      enableMarginRate,
      parentId,
      children,
      creativeTypes,
      friendlyName,
      enableFormatSelection,
      availableFormats,
      defaultGeoTarget
    },
    setState
  ] = useState(initialState);
  const [{ errorKey, errorName, errorType }, setError] = useState(ErrorState);
  const [formatOptions, setFormatOptions] = useState([]);

  useEffect(() => {
    const getFormatOptions = async () => {
      if (!config?.key) {
        return;
      }
      const acceptedCreatives = await ACTIVATION.getMediaAcceptedCreativesByMedias(
        [config.key]
      );
      setFormatOptions(
        acceptedCreatives.reduce((options, creative) => {
          if (creative.name) {
            options.push({
              label: creative.ratio
                ? `${creative.name} (ratio : ${creative.ratio})`
                : creative.name,
              value: creative._id
            });
          }
          return options;
        }, [])
      );
    };
    getFormatOptions();
  }, [config]);

  const selectedFormats = availableFormats.reduce((selected, id) => {
    const option = formatOptions.find(el => el.value === id);
    if (option) {
      selected.push(option);
    }
    return selected;
  }, []);

  const toggleErrorKey = state => {
    setError(prevState => ({ ...prevState, errorKey: state }));
  };

  const toggleErrorName = state => {
    setError(prevState => ({ ...prevState, errorName: state }));
  };

  const toggleErrorTypes = state => {
    setError(prevState => ({ ...prevState, errorType: state }));
  };

  const toggleErrorCreativeTypes = state => {
    setError(prevState => ({ ...prevState, errorCreativeTypes: state }));
  };

  const handleChange = field => value => {
    let newValue = typeof value === 'string' ? value.trim() : value;
    if (field === 'name') {
      toggleErrorName(!newValue);
    }

    if (field === 'types') {
      newValue = value.map(el => {
        if (typeof el === 'string') {
          return el;
        }
        return el.value;
      });
    }
    if (field === 'creativeTypes') {
      newValue = value.map(el => {
        if (typeof el === 'string') {
          return el;
        }
        return el.value;
      });
    }
    setState(prevState => ({
      ...prevState,
      [field]: newValue
    }));
  };

  const handleChangeParent = value => {
    setState(prevState => ({
      ...prevState,
      parentId: parentList.find(f => f.key === value[0]?.key)?.id || '',
      children: []
    }));
  };

  const handleChangeChildren = value => {
    setState(prevState => ({
      ...prevState,
      parent: [],
      children: value,
      selectedCriterias: []
    }));
  };

  const resetStates = () => {
    setState(initialState);
    setError(ErrorState);
  };

  const doAction = () => {
    let error = false;

    if (!name) {
      toggleErrorName(true);
      error = true;
    }
    if (!key) {
      toggleErrorKey(true);
      error = true;
    }
    if (types.length === 0) {
      toggleErrorTypes(true);
      error = true;
    }
    if (creativeTypes.length === 0) {
      toggleErrorCreativeTypes(true);
      error = true;
    }
    if (!error) {
      const datas = {
        name,
        model,
        key,
        types,
        channels: types,
        creativeTypes,
        minimumBudget,
        minimumAudience,
        dailyMinimumBudget,
        documentation: documentation.reduce((acc, doc) => {
          acc[langues.find(langue => langue.label === doc.langue).key] =
            doc.url;
          return acc;
        }, {}),
        parentId,
        children: children.map(child => child.id),
        page,
        friendlyName,
        enableFormatSelection,
        availableFormats,
        enableMarginRate,
        geoTargets: geoTargetOptions.map(({ value, label, ...geoTarget }) => ({
          ...geoTarget,
          isDefault: defaultGeoTarget === value
        }))
      };

      onValidate(datas);
      resetStates();
    }
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              variant="contained"
              size="medium"
              widthSize="medium"
              onClick={doAction}
            >
              {actionName} le média activable
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const getClearIcon = () => {
    return <ClearIcon style={{ color: themeColors.light }} />;
  };

  return (
    <Modal
      actions={getActions()}
      {...props}
      onClose={() => {
        resetStates();
        onClose();
      }}
    >
      <Container>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Média" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Nom du média"
                          required
                          placeholder=""
                          error={errorName}
                          onChange={handleChange('name')}
                          value={name}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="key"
                          required
                          placeholder=""
                          error={errorKey}
                          onChange={handleChange('key')}
                          value={key}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Select
                          title="Connecteur"
                          options={connexionsGenerator.map(el => el.name)}
                          onChange={handleChange('model')}
                          value={model}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Budget minimum par jour"
                          placeholder=""
                          onChange={handleChange('dailyMinimumBudget')}
                          value={dailyMinimumBudget}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Budget minimum"
                          onChange={handleChange('minimumBudget')}
                          value={minimumBudget}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Audience minimum"
                          placeholder=""
                          onChange={handleChange('minimumAudience')}
                          value={minimumAudience}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Abbréviation"
                          placeholder=""
                          onChange={handleChange('friendlyName')}
                          value={friendlyName}
                        />
                      </Item>
                    </Row>
                  </Item>
                  {geoTargetOptions.length > 0 && (
                    <Item xs={6} justify="flex-start">
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Type de ciblage par défaut"
                            options={geoTargetOptions}
                            onChange={handleChange('defaultGeoTarget')}
                            value={defaultGeoTarget}
                          />
                        </Item>
                      </Row>
                    </Item>
                  )}
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={3} justify="flex-start">
                        <Switch
                          title="Page"
                          onChange={handleChange('page')}
                          checked={page}
                        />
                      </Item>
                      <Item xs={3} justify="flex-start">
                        <Switch
                          title="Marge"
                          onChange={handleChange('enableMarginRate')}
                          checked={enableMarginRate}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Canaux*" xs={3} error={errorType}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item>
                    <ListField
                      options={channels.map(el => {
                        return {
                          label: el?.label,
                          value: el?.key
                        };
                      })}
                      searchOptionsKeys={['label']}
                      values={types.map(el => {
                        const value = channels.find(
                          channel => channel.key === el
                        );
                        return { label: value?.label, value: value?.key };
                      })}
                      onChange={handleChange('types')}
                      actionTxt="Ajouter"
                      deleteAction
                    />
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Type de créatives*" xs={3} error={errorType}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item>
                    <ListField
                      options={creativeTypesOptions}
                      searchOptionsKeys={['label']}
                      values={creativeTypes.map(type =>
                        creativeTypesOptions.find(el => el.value === type)
                      )}
                      onChange={handleChange('creativeTypes')}
                      actionTxt="Ajouter"
                      deleteAction
                    />
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        {model === 'dv360' && (
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Formats" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item>
                      <Switch
                        title="Activer la sélection de formats"
                        onChange={handleChange('enableFormatSelection')}
                        checked={enableFormatSelection}
                      />
                    </Item>
                  </Row>
                  {enableFormatSelection && (
                    <>
                      <Row spacing={0} justify="flex-start">
                        <Item>
                          <AsyncAutocomplete
                            multiple
                            disableCloseOnSelect
                            ChipProps={{
                              sx: { backgroundColor: designSystem.amberBg }
                            }}
                            title="Formats disponibles pour ce média"
                            placeholder="Sélectionnez un ou plusieurs formats…"
                            options={formatOptions}
                            value={selectedFormats}
                            onChange={value =>
                              handleChange('availableFormats')(
                                value.map(el => el.value)
                              )
                            }
                            formatLabel={option => option?.label || ''}
                          />
                        </Item>
                      </Row>
                      {availableFormats.length === 0 && (
                        <Row spacing={0} justify="flex-start">
                          <Item>
                            <Alert
                              severity="warning"
                              bgColor={designSystem.amberBg}
                              sx={{ mt: 2 }}
                              text="Aucun format sélectionné : tous les formats sont sélectionnés par défaut et seront affichés pour ce média."
                            />
                          </Item>
                        </Row>
                      )}
                    </>
                  )}
                </Container>
              </FormSection>
            </Item>
          </Row>
        )}
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Documentation" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item>
                    <ListField
                      labels={['Langue', 'Url']}
                      options={['langue', 'url']}
                      inputs={['Select', 'TextField']}
                      isRequireds={[false, false]}
                      values={documentation}
                      formatedValues={[langues]}
                      fieldsFormat={[langues]}
                      selectNbMax={2}
                      valueUnique
                      variant="customFields"
                      onChange={handleChange('documentation')}
                      actionTxt="Ajouter"
                      deleteAction
                    />
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={0}>
          <Item justify="flex-start">
            <FormSection title="Média parent">
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item justify="flex-start">
                    {children.length !== 0 ? (
                      <Text>
                        Vous ne pouvez pas sélectionner de media parent
                        lorsqu&lsquo;un média enfant est défini.
                      </Text>
                    ) : (
                      <ListField
                        options={parentList}
                        values={parentList.filter(el => el.id === parentId)}
                        searchOptionsKeys={['name', 'key']}
                        hideKeys={['id']}
                        onChange={handleChangeParent}
                        actionTxt="Ajouter un parent"
                        placeholder=""
                        deleteAction
                        textFieldOnTop
                        selectNbMax={1}
                        deleteIcon={getClearIcon()}
                      />
                    )}
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={0}>
          <Item justify="flex-start">
            <FormSection title="Médias enfants">
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item justify="flex-start">
                    {parentId ? (
                      <Text>
                        Vous ne pouvez pas sélectionner de medias enfants
                        lorsqu&lsquo;un média parent est défini.
                      </Text>
                    ) : (
                      <ListField
                        options={childrenList}
                        values={children}
                        searchOptionsKeys={['name', 'key']}
                        hideKeys={['id', 'parentId']}
                        onChange={handleChangeChildren}
                        actionTxt="Ajouter un enfant"
                        placeholder=""
                        deleteAction
                        useWarning
                        warningColor="global"
                        textFieldOnTop
                        deleteIcon={getClearIcon()}
                      />
                    )}
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
      </Container>
    </Modal>
  );
};

export default ModalForm;
